import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export interface UserSettings {
  id: string;
  email_notifications: boolean;
  push_notifications: boolean;
  marketing_emails: boolean;
  two_factor_enabled: boolean;
  last_password_change?: string;
  phone_verified: boolean;
  preferred_language: string;
  timezone: string;
  created_at: string;
  updated_at: string;
}

export interface UpdateUserSettingsInput {
  email_notifications?: boolean;
  push_notifications?: boolean;
  marketing_emails?: boolean;
  two_factor_enabled?: boolean;
  phone_verified?: boolean;
  preferred_language?: string;
  timezone?: string;
}

interface UseUserSettingsProps {
  userId: string;
  enabled?: boolean;
}

export function useUserSettings({ userId, enabled = true }: UseUserSettingsProps) {
  const queryClient = useQueryClient();
  const { user: authUser } = useAuth();

  const { data: settings, isLoading, error } = useQuery({
    queryKey: ["userSettings", userId],
    queryFn: async () => {
      if (!userId) throw new Error("User ID is required");

      // First try to get existing settings
      const { data: existingSettings, error: fetchError } = await supabase
        .from("user_settings")
        .select("*")
        .eq("id", userId)
        .maybeSingle();

      if (fetchError) throw fetchError;

      // If settings don't exist, create them with defaults
      if (!existingSettings) {
        const defaults = {
          id: userId,
          email_notifications: true,
          push_notifications: false,
          marketing_emails: false,
          two_factor_enabled: false,
          phone_verified: false,
          preferred_language: "en",
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };

        const { data: newSettings, error: createError } = await supabase
          .from("user_settings")
          .insert([defaults])
          .select()
          .single();

        if (createError) throw createError;
        return newSettings as UserSettings;
      }

      return existingSettings as UserSettings;
    },
    enabled: enabled && !!userId && !!authUser,
  });

  const updateSettings = useMutation({
    mutationFn: async (updates: UpdateUserSettingsInput) => {
      if (!userId) throw new Error("User ID is required");

      const { error } = await supabase
        .from("user_settings")
        .update(updates)
        .eq("id", userId);

      if (error) throw error;

      // Log the settings change
      await supabase.rpc("log_security_event", {
        p_user_id: userId,
        p_event_type: "settings_update",
        p_metadata: { updates }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userSettings", userId] });
      toast.success("Settings updated successfully");
    },
    onError: (error) => {
      console.error("Error updating settings:", error);
      toast.error("Failed to update settings");
    },
  });

  const updatePassword = useMutation({
    mutationFn: async ({ currentPassword, newPassword }: { currentPassword: string; newPassword: string }) => {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) throw error;

      // Log the password change
      await supabase.rpc("log_security_event", {
        p_user_id: userId,
        p_event_type: "password_change",
        p_metadata: { timestamp: new Date().toISOString() }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userSettings", userId] });
      toast.success("Password updated successfully");
    },
    onError: (error) => {
      console.error("Error updating password:", error);
      toast.error("Failed to update password");
    },
  });

  const toggleTwoFactor = useMutation({
    mutationFn: async () => {
      if (!settings) throw new Error("Settings not loaded");

      const newState = !settings.two_factor_enabled;
      const { error } = await supabase
        .from("user_settings")
        .update({ two_factor_enabled: newState })
        .eq("id", userId);

      if (error) throw error;

      // Log the 2FA change
      await supabase.rpc("log_security_event", {
        p_user_id: userId,
        p_event_type: "two_factor_toggle",
        p_metadata: { enabled: newState }
      });

      return newState;
    },
    onSuccess: (newState) => {
      queryClient.invalidateQueries({ queryKey: ["userSettings", userId] });
      toast.success(`Two-factor authentication ${newState ? "enabled" : "disabled"}`);
    },
    onError: (error) => {
      console.error("Error toggling two-factor:", error);
      toast.error("Failed to update two-factor authentication");
    },
  });

  return {
    settings,
    isLoading,
    error,
    updateSettings,
    updatePassword,
    toggleTwoFactor,
  };
} 